import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const newPalletImages = [
  'https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png',
  'https://public.readdy.ai/ai/img_res/d91eb2a95b52a1a63f0113627dd0b5eb.jpg',
  'https://readdy.ai/api/search-image?query=stacked%20new%20wooden%20pallets%20neat%20rows%20warehouse%20storage%20facility%20Belgium%20logistics%20fresh%20timber&width=800&height=600&seq=np3&orientation=landscape',
];

const pricingTiers = [
  { range: '10-100', price: 24 },
  { range: '100-500', price: 19 },
  { range: '500-5000', price: 16 },
  { range: '5000+', price: 14 },
];

function getCurrentPrice(qty: number): number {
  if (qty >= 5000) return 14;
  if (qty >= 500) return 16;
  if (qty >= 100) return 19;
  return 24;
}

function getCurrentTierIndex(qty: number): number {
  if (qty >= 5000) return 3;
  if (qty >= 500) return 2;
  if (qty >= 100) return 1;
  return 0;
}

function OrderModal({ onClose, quantity, setQuantity }: { onClose: () => void; quantity: number; setQuantity: (q: number) => void }) {
  const { t } = useTranslation();
  const images = newPalletImages;

  const scrollToBanking = () => {
    onClose();
    setTimeout(() => {
      const el = document.getElementById('banking-info');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.style.border = '2px solid #f59e0b';
        setTimeout(() => { el.style.border = ''; }, 2000);
      }
    }, 300);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">New Wooden Pallets</h3>
              <p className="text-sm text-gray-600 mt-1">ISPM15 heat-treated, export ready, virgin timber. Bulk pricing from €10–€20. PEL Partner certified.</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none cursor-pointer">×</button>
          </div>

          <div className="mb-6">
            <img src={images[0]} alt="New Wooden Pallets" className="w-full h-64 object-cover rounded-lg" />
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">{t('calc_quantity')}</label>
              <input
                type="number"
                min="10"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(10, parseInt(e.target.value) || 10))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-600">{t('calc_price_per')}:</span>
                <span className="text-xl font-bold text-teal-600">€{getCurrentPrice(quantity)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-900 font-semibold">{t('calc_total')}</span>
                <span className="text-2xl font-bold text-gray-900">€{(getCurrentPrice(quantity) * quantity).toLocaleString()}</span>
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-semibold text-gray-700">{t('calc_volume_pricing')}:</p>
              {pricingTiers.map((tier, index) => (
                <div
                  key={tier.range}
                  className={`flex justify-between items-center p-3 rounded-lg ${
                    getCurrentTierIndex(quantity) === index ? 'bg-teal-50 border-2 border-teal-500' : 'bg-gray-50'
                  }`}
                >
                  <span className="text-sm text-gray-700">{tier.range} {t('calc_pieces')}</span>
                  <span className="font-semibold text-gray-900">€{tier.price}/{t('calc_pallets').replace(/s$/, '')}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-700 mb-2">{t('calc_payment_methods_label')}</p>
            <button onClick={scrollToBanking} className="w-full px-6 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer">
              <i className="ri-bank-card-line text-xl"></i>
              <span className="font-semibold">Credit Card</span>
            </button>
            <button onClick={scrollToBanking} className="w-full px-6 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer">
              <i className="ri-google-fill text-xl"></i>
              <span className="font-semibold">Google Pay</span>
            </button>
            <button onClick={scrollToBanking} className="w-full px-6 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer">
              <i className="ri-paypal-line text-xl"></i>
              <span className="font-semibold">PayPal</span>
            </button>
            <button onClick={scrollToBanking} className="w-full px-6 py-3 bg-white border-2 border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer">
              <i className="ri-bank-line text-xl"></i>
              <span className="font-semibold">Bancontact</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function NewPalletsPage() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [quantity, setQuantity] = useState(10);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showOrderModal, setShowOrderModal] = useState(false);

  const images = newPalletImages;

  const scrollToContact = () => {
    const el = document.getElementById('contact-section');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToBanking = () => {
    const el = document.getElementById('banking-info');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.style.border = '2px solid #f59e0b';
      setTimeout(() => { el.style.border = ''; }, 2000);
    }
  };

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    document.title = 'New Wooden Pallets Antwerp | ISPM15 Export Ready | PEL Partner Belgium';

    const setMeta = (selector: string, nameAttr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (el) { el.setAttribute('content', value); }
      else {
        el = document.createElement('meta');
        el.setAttribute(nameAttr, selector.match(/\[(?:name|property)="([^"]+)"\]/)?.[1] ?? '');
        el.setAttribute('content', value);
        document.head.appendChild(el);
      }
    };

    setMeta('meta[name="description"]', 'name', 'Brand new wooden pallets in Antwerp, Belgium. ISPM15 heat-treated, export ready, virgin timber. Custom sizes available. Prices from €10/pallet. Fast delivery across Belgium. Contact +32 487 26 19 70.');
    setMeta('meta[name="keywords"]', 'name', 'new pallets, ISPM15 pallets, export pallets, virgin timber pallets, custom pallets Antwerp, heat-treated pallets Belgium');
    setMeta('meta[name="robots"]', 'name', 'index, follow');

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };
    setOg('og:title', 'New Wooden Pallets Antwerp | ISPM15 Export Ready | PEL Partner Belgium');
    setOg('og:description', 'Brand new wooden pallets in Antwerp, Belgium. ISPM15 heat-treated, export ready, virgin timber. Custom sizes available. Prices from €10/pallet.');
    setOg('og:url', `${siteUrl}/pallets/new`);
    setOg('og:type', 'product');
    setOg('og:image', 'https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png');

    const setTwitter = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (el) { el.setAttribute('content', content); }
      else {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        el.setAttribute('content', content);
        document.head.appendChild(el);
      }
    };
    setTwitter('twitter:card', 'summary_large_image');
    setTwitter('twitter:title', 'New Wooden Pallets Antwerp | ISPM15 Export Ready | PEL Partner');
    setTwitter('twitter:description', 'Brand new wooden pallets in Antwerp, Belgium. ISPM15 heat-treated, export ready, virgin timber. Custom sizes available. Prices from €10/pallet.');
    setTwitter('twitter:image', 'https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png');

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) { canonical.setAttribute('href', `${siteUrl}/pallets/new`); }
    else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', `${siteUrl}/pallets/new`);
      document.head.appendChild(canonical);
    }

    // Schema.org JSON-LD
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'New Wooden Euro Pallets',
      description: 'Brand new ISPM15 heat-treated wooden pallets made from virgin timber. Export ready, custom sizes available. Supplied from Antwerp, Belgium.',
      image: 'https://public.readdy.ai/ai/img_res/a0b5b946-f08e-472c-9146-e6f01453ec3f.png',
      url: `${siteUrl}/pallets/new`,
      brand: { '@type': 'Brand', name: 'PEL Partner' },
      category: 'Industrial Pallets',
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Dimensions', value: '1200 × 800 mm (custom available)' },
        { '@type': 'PropertyValue', name: 'Load Capacity', value: 'Up to 2000 kg' },
        { '@type': 'PropertyValue', name: 'Certification', value: 'ISPM15 Heat-Treated' },
        { '@type': 'PropertyValue', name: 'Material', value: 'Virgin Timber' },
      ],
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'EUR',
        lowPrice: '14',
        highPrice: '24',
        offerCount: '4',
        availability: 'https://schema.org/InStock',
        seller: { '@type': 'Organization', name: 'PEL Partner', url: siteUrl },
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Products', item: `${siteUrl}/#new-pallets` },
        { '@type': 'ListItem', position: 3, name: 'New Wooden Pallets', item: `${siteUrl}/pallets/new` },
      ],
    };

    const webPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'New Wooden Euro Pallets – PEL Partner Antwerp',
      description: 'Brand new ISPM15 heat-treated wooden pallets from Antwerp, Belgium. Prices from €10/pallet.',
      url: `${siteUrl}/pallets/new`,
      inLanguage: 'en',
      isPartOf: { '@type': 'WebSite', url: siteUrl, name: 'PEL Partner' },
      breadcrumb: breadcrumbSchema,
    };

    const schemas = [productSchema, breadcrumbSchema, webPageSchema];
    const scriptEls: HTMLScriptElement[] = [];
    schemas.forEach((schema, i) => {
      const existing = document.getElementById(`schema-new-pallets-${i}`);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      script.id = `schema-new-pallets-${i}`;
      document.head.appendChild(script);
      scriptEls.push(script);
    });

    return () => { scriptEls.forEach((s) => { if (s.parentNode) s.parentNode.removeChild(s); }); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-stack-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">PEL Partner</span>
            </a>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Home</a>
              <a href="/pallets/grade-a" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Grade A</a>
              <a href="/pallets/grade-b" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">Grade B</a>
              <a href="/pallets/new" className="text-teal-600 font-semibold whitespace-nowrap cursor-pointer">New Pallets</a>
              <a href="/#contact-section" className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <button
              className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl text-gray-900`}></i>
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-6 pb-4 flex flex-col gap-4">
            <a href="/" onClick={() => setMenuOpen(false)} className="text-gray-700 hover:text-teal-600 text-sm cursor-pointer">Home</a>
            <a href="/pallets/grade-a" onClick={() => setMenuOpen(false)} className="text-gray-700 hover:text-teal-600 text-sm cursor-pointer">Grade A</a>
            <a href="/pallets/grade-b" onClick={() => setMenuOpen(false)} className="text-gray-700 hover:text-teal-600 text-sm cursor-pointer">Grade B</a>
            <a href="/pallets/new" onClick={() => setMenuOpen(false)} className="text-teal-600 font-semibold text-sm cursor-pointer">New Pallets</a>
          </div>
        )}
      </nav>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-2 text-sm">
            <a href="/" className="text-gray-600 hover:text-teal-600 cursor-pointer">Home</a>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-600">Products</span>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-semibold">New Wooden Pallets</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <i className="ri-star-fill"></i>
                <span>{t('grade_new_label').toUpperCase()}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">{t('new_title')}</h1>
              <p className="text-xl text-gray-600 mb-8">{t('new_subtitle')}</p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <i className="ri-shield-check-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">ISPM15</div>
                    <div className="text-sm text-gray-600">{t('why1_title')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <i className="ri-plant-line text-emerald-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Virgin Timber</div>
                    <div className="text-sm text-gray-600">{t('product_wood')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-global-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Export Ready</div>
                    <div className="text-sm text-gray-600">{t('service4_title')}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <i className="ri-ruler-line text-purple-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{t('service3_title')}</div>
                    <div className="text-sm text-gray-600">{t('gallery_custom')}</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => setShowOrderModal(true)}
                  className="px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold whitespace-nowrap cursor-pointer"
                >
                  {t('product_order_now')}
                </button>
                <button
                  onClick={scrollToContact}
                  className="px-8 py-4 bg-white border-2 border-gray-300 text-gray-900 rounded-lg hover:border-teal-600 hover:text-teal-600 transition-colors font-semibold whitespace-nowrap cursor-pointer"
                >
                  {t('product_request_quote')}
                </button>
              </div>
            </div>

            <div>
              <div className="relative">
                <img
                  src={images[currentImageIndex]}
                  alt="New Wooden Pallets"
                  className="w-full h-96 object-cover object-top rounded-2xl shadow-2xl"
                />
                <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-lg shadow-lg">
                  <div className="text-sm text-gray-600">Starting from</div>
                  <div className="text-2xl font-bold text-teal-600">€24</div>
                  <div className="text-xs text-gray-500">per pallet</div>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-1 h-20 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      currentImageIndex === index ? 'border-teal-600' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-cover object-top" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('calc_volume_pricing')}</h2>
            <p className="text-lg text-gray-600">{t('calc_volume_hint')}</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white border-2 border-gray-200 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">{t('pricing_title')}</h3>
              <div className="space-y-4">
                {pricingTiers.map((tier, index) => (
                  <div
                    key={tier.range}
                    className={`flex justify-between items-center p-4 rounded-lg transition-all ${
                      getCurrentTierIndex(quantity) === index ? 'bg-teal-50 border-2 border-teal-500' : 'bg-gray-50'
                    }`}
                  >
                    <div>
                      <div className="font-semibold text-gray-900">{tier.range} {t('calc_pieces')}</div>
                      <div className="text-sm text-gray-600">{t('calc_bulk_discount')}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-teal-600">€{tier.price}</div>
                      <div className="text-sm text-gray-600">{t('calc_per_pallet')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">{t('calc_calculate')}</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{t('calc_quantity')}</label>
                  <input
                    type="number"
                    min="10"
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(10, parseInt(e.target.value) || 10))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-600 mt-1">{t('calc_min_order')}</p>
                </div>

                <div className="bg-white p-6 rounded-lg">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-600">{t('calc_current_price')}:</span>
                    <span className="text-xl font-bold text-teal-600">€{getCurrentPrice(quantity)}</span>
                  </div>
                  <div className="text-gray-500 text-xs text-right mb-3">
                    ({quantity} × €{getCurrentPrice(quantity)}) — {t('calc_volume_hint')}
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-gray-600">{t('calc_quantity')}:</span>
                    <span className="text-lg font-semibold text-gray-900">{quantity} {t('calc_pallets')}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-900 font-semibold">{t('calc_total')}</span>
                      <span className="text-3xl font-bold text-gray-900">€{(getCurrentPrice(quantity) * quantity).toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700 mb-2">{t('calc_payment_methods_label')}</p>
                  <button
                    onClick={scrollToBanking}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-bank-card-line text-lg"></i>
                    <span className="font-medium text-sm">Credit Card</span>
                  </button>
                  <button
                    onClick={scrollToBanking}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-google-fill text-lg"></i>
                    <span className="font-medium text-sm">Google Pay</span>
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={scrollToBanking}
                      className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-paypal-line text-lg"></i>
                      <span className="font-medium text-sm">PayPal</span>
                    </button>
                    <button
                      onClick={scrollToBanking}
                      className="px-4 py-3 bg-white border border-gray-300 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-bank-line text-lg"></i>
                      <span className="font-medium text-sm">Bancontact</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Technical Specifications */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('product_tech_specs')}</h2>
            <p className="text-lg text-gray-600">{t('new_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-ruler-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('product_dimensions')}</h3>
              <p className="text-gray-600">1200 × 800 mm (Standard)</p>
              <p className="text-sm text-gray-500 mt-1">{t('service3_desc')}</p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-weight-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('product_load')}</h3>
              <p className="text-gray-600">Up to 2000 kg</p>
              <p className="text-sm text-gray-500 mt-1">Dynamic load rating</p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-blue-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('why1_title')}</h3>
              <p className="text-gray-600">ISPM15 Heat-Treated</p>
              <p className="text-sm text-gray-500 mt-1">{t('faq_a6')}</p>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-plant-line text-purple-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('product_wood')}</h3>
              <p className="text-gray-600">Virgin Timber</p>
              <p className="text-sm text-gray-500 mt-1">{t('why2_desc')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('product_key_features')}</h2>
            <p className="text-lg text-gray-600">{t('quality_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-star-fill text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('grade_new_label')}</h3>
              <p className="text-gray-600">{t('new_subtitle')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-emerald-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Export Ready</h3>
              <p className="text-gray-600">{t('faq_a6')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-settings-3-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('service3_title')}</h3>
              <p className="text-gray-600">{t('service3_desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('why1_title')}</h3>
              <p className="text-gray-600">{t('why1_desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-truck-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('service4_title')}</h3>
              <p className="text-gray-600">{t('service4_desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-plant-line text-pink-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">{t('why4_title')}</h3>
              <p className="text-gray-600">{t('why4_desc')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Description */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">About Our New Wooden Pallets</h2>
          <div className="text-gray-600 space-y-4 text-lg leading-relaxed">
            <p>
              Our brand new wooden euro pallets are manufactured from premium virgin timber to the highest quality standards. Each pallet is ISPM15 heat-treated, making them fully compliant with international export regulations and ready for worldwide shipping.
            </p>
            <p>
              Perfect for businesses that require pristine condition pallets for high-value products, food-grade applications, or international export. The virgin timber construction ensures maximum strength, durability, and a professional appearance that represents your brand well.
            </p>
            <p>
              We offer standard 1200×800mm euro pallets as well as custom sizes to meet your specific requirements. Whether you need pallets for warehouse storage, retail display, or international shipping, our new wooden pallets provide the reliability and quality you demand.
            </p>
            <p>
              With competitive bulk pricing starting from €14 per pallet for large orders, you get premium quality at excellent value. All pallets are manufactured in compliance with EPAL standards and come with full certification documentation.
            </p>
          </div>
        </div>
      </div>

      {/* Related Products */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t('product_view_details')}</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <a href="/pallets/grade-a" className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 hover:shadow-lg transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-star-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('form_product_a')}</h3>
                  <p className="text-gray-600 mb-3">{t('faq_a1')}</p>
                  <div className="text-teal-600 font-semibold">From €11.5 {t('calc_per_pallet')} →</div>
                </div>
              </div>
            </a>

            <a href="/pallets/grade-b" className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-teal-500 hover:shadow-lg transition-all cursor-pointer">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-price-tag-3-line text-emerald-600 text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{t('form_product_b')}</h3>
                  <p className="text-gray-600 mb-3">{t('used_subtitle')}</p>
                  <div className="text-teal-600 font-semibold">From €10 {t('calc_per_pallet')} →</div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact-section" className="py-16 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('contact_title')}</h2>
            <p className="text-lg text-gray-600">{t('contact_info_title')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <a href="tel:+32487261970" className="bg-white p-6 rounded-xl border border-gray-200 hover:border-teal-500 hover:shadow-lg transition-all text-center cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-teal-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('contact_call_now')}</h3>
              <p className="text-teal-600 font-semibold">+32 487 26 19 70</p>
            </a>

            <a href="mailto:pelpartnercompany@gmail.com" className="bg-white p-6 rounded-xl border border-gray-200 hover:border-teal-500 hover:shadow-lg transition-all text-center cursor-pointer">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('contact_email_us')}</h3>
              <p className="text-teal-600 font-semibold">pelpartnercompany@gmail.com</p>
            </a>

            <a href="https://wa.me/32487261970" target="_blank" rel="noopener noreferrer" className="bg-white p-6 rounded-xl border border-gray-200 hover:border-teal-500 hover:shadow-lg transition-all text-center cursor-pointer">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-whatsapp-line text-green-600 text-xl"></i>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-teal-600 font-semibold">{t('hero_cta_quote')}</p>
            </a>
          </div>
        </div>
      </div>

      {/* Banking Information */}
      <div id="banking-info" className="py-16 bg-white transition-all duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Banking Information</h2>
            <p className="text-lg text-gray-600">Direct bank transfer details</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-bank-line text-teal-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">EUR Account</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-600">EUR IBAN</div>
                  <div className="font-bold text-gray-900 font-mono bg-white px-3 py-2 rounded border border-gray-200">LT71 3250 0690 5068 8936</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">BIC/SWIFT</div>
                  <div className="font-bold text-gray-900 font-mono bg-white px-3 py-2 rounded border border-gray-200">REVOLT21</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <i className="ri-bank-line text-emerald-600 text-xl"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900">GBP Account</h3>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-600">Sort Code</div>
                  <div className="font-bold text-gray-900 font-mono bg-white px-3 py-2 rounded border border-gray-200">23-01-20</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Account Number</div>
                  <div className="font-bold text-gray-900 font-mono bg-white px-3 py-2 rounded border border-gray-200">87899767</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
            <div className="flex gap-3">
              <i className="ri-information-line text-blue-600 text-xl flex-shrink-0 mt-1"></i>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Payment Instructions</h4>
                <p className="text-gray-600 text-sm">Please include your order number in the payment reference. Orders are processed upon payment confirmation. For large orders, contact us for payment terms.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-600 to-emerald-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-stack-line text-white text-xl"></i>
                </div>
                <span className="text-xl font-bold">PEL Partner</span>
              </div>
              <p className="text-white/80 text-sm">{t('footer_desc')}</p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer_products')}</h4>
              <div className="space-y-2 text-sm">
                <a href="/pallets/grade-a" className="block text-white/80 hover:text-white transition-colors cursor-pointer">{t('form_product_a')}</a>
                <a href="/pallets/grade-b" className="block text-white/80 hover:text-white transition-colors cursor-pointer">{t('form_product_b')}</a>
                <a href="/pallets/new" className="block text-white/80 hover:text-white transition-colors cursor-pointer">{t('form_product_new')}</a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('footer_contact')}</h4>
              <div className="space-y-2 text-sm text-white/80">
                <p>{t('about_location')}</p>
                <p>+32 487 26 19 70</p>
                <p>pelpartnercompany@gmail.com</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">{t('contact_hours_label')}</h4>
              <div className="space-y-2 text-sm text-white/80">
                <p>{t('contact_hours_val')}</p>
                <p>{t('about_support')}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 text-center text-sm text-white/80">
            <p>{t('footer_copyright')}</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/32487261970"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all z-50 cursor-pointer"
        aria-label="Contact us on WhatsApp"
      >
        <i className="ri-whatsapp-line text-2xl"></i>
      </a>

      {showOrderModal && (
        <OrderModal
          onClose={() => setShowOrderModal(false)}
          quantity={quantity}
          setQuantity={setQuantity}
        />
      )}
    </div>
  );
}
